import React, { useState, useEffect } from "react";
import styled from "styled-components";
import LogoImage from "../assets/Logo.jpg";

const HeaderContainer = styled.header`
  background: ${(props) =>
    props.isScrolled
      ? "rgba(255, 255, 255, 0.95)"
      : "rgba(255, 255, 255, 0.98)"};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: ${(props) =>
    props.isScrolled
      ? "0 8px 32px rgba(0, 191, 255, 0.15)"
      : "0 2px 20px rgba(0, 191, 255, 0.08)"};
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid rgba(0, 191, 255, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 191, 255, 0.5),
      transparent
    );
    opacity: ${(props) => (props.isScrolled ? 1 : 0)};
    transition: opacity 0.4s ease;
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.2rem 20px;
  position: relative;
  min-height: 70px;

  @media (max-width: 768px) {
    padding: 1rem 16px;
    min-height: 60px;
  }

  @media (max-width: 480px) {
    padding: 0.8rem 12px;
    min-height: 56px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--primary-blue);
  cursor: pointer;
  z-index: 1001;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  .logo-image {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    margin-right: 14px;
    object-fit: cover;
    box-shadow: 0 8px 25px rgba(0, 191, 255, 0.25);
    border: 3px solid rgba(255, 255, 255, 0.8);
    background: white;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        45deg,
        transparent,
        rgba(0, 191, 255, 0.1),
        transparent
      );
      transform: rotate(45deg);
      transition: transform 0.6s ease;
    }

    &:hover::before {
      transform: rotate(45deg) translate(50%, 50%);
    }
  }

  .logo-text {
    font-weight: 900;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #00bfff, #1e90ff, #0066cc);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 2px;
      background: linear-gradient(90deg, #00bfff, #1e90ff);
      transition: width 0.3s ease;
    }

    &:hover::after {
      width: 100%;
    }
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;

    .logo-image {
      width: 44px;
      height: 44px;
      margin-right: 10px;
    }
  }
`;

const DesktopNavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  padding: 0.5rem;
  border: 1px solid rgba(0, 191, 255, 0.1);

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.button`
  background: ${(props) => (props.active ? "white" : "transparent")};
  border: none;
  color: ${(props) =>
    props.active ? "var(--primary-blue)" : "var(--text-secondary)"};
  font-weight: ${(props) => (props.active ? "600" : "500")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  padding: 0.8rem 1.5rem;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.95rem;
  border-radius: 25px;
  white-space: nowrap;
  overflow: hidden;
  box-shadow: ${(props) =>
    props.active ? "0 4px 15px rgba(0, 191, 255, 0.2)" : "none"};

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.8),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    color: var(--primary-blue);
    background: ${(props) =>
      props.active ? "white" : "rgba(255, 255, 255, 0.8)"};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 191, 255, 0.15);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const ContactButton = styled.button`
  background: linear-gradient(135deg, #00bfff, #1e90ff, #0066cc);
  color: white;
  padding: 0.8rem 2rem;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 6px 20px rgba(0, 191, 255, 0.3);
  border: none;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  margin-left: 1rem;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.5s ease;
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.3) 0%,
      transparent 70%
    );
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: width 0.6s ease, height 0.6s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(0, 191, 255, 0.4);
    background: linear-gradient(135deg, #1e90ff, #0066cc, #004499);

    &::before {
      left: 100%;
    }

    &::after {
      width: 300px;
      height: 300px;
    }
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const SidebarToggle = styled.button`
  display: none;
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(0, 191, 255, 0.2);
  color: var(--primary-blue);
  font-size: 1.4rem;
  padding: 0.6rem;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
  position: relative;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 191, 255, 0.15);
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(0, 191, 255, 0.1);
    border-color: var(--primary-blue);
    transform: scale(1.1) rotate(90deg);
    box-shadow: 0 6px 20px rgba(0, 191, 255, 0.25);
  }

  &:active {
    transform: scale(0.95) rotate(90deg);
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
  }

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
    font-size: 1.2rem;
  }
`;

const SidebarOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  z-index: 1998;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 769px) {
    display: none;
  }
`;

const Sidebar = styled.div`
  position: fixed;
  top: 0;
  right: ${(props) => (props.isOpen ? "0" : "-350px")};
  width: 350px;
  height: 100vh;
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 255, 255, 0.9)
  );
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  z-index: 1999;
  transition: right 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: -8px 0 40px rgba(0, 191, 255, 0.15);
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(0, 191, 255, 0.1);

  @media (min-width: 769px) {
    display: none;
  }

  @media (max-width: 480px) {
    width: 300px;
    right: ${(props) => (props.isOpen ? "0" : "-300px")};
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 191, 255, 0.15);
  background: linear-gradient(
    135deg,
    rgba(0, 191, 255, 0.05),
    rgba(30, 144, 255, 0.05)
  );
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 191, 255, 0.3),
      transparent
    );
  }
`;

const SidebarLogo = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--primary-blue);

  .logo-image {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    margin-right: 10px;
    object-fit: cover;
    box-shadow: 0 4px 15px rgba(0, 191, 255, 0.25);
    border: 2px solid rgba(255, 255, 255, 0.8);
    background: white;
  }

  .logo-text {
    font-weight: 900;
    letter-spacing: -0.5px;
    background: linear-gradient(135deg, #00bfff, #1e90ff, #0066cc);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const SidebarCloseButton = styled.button`
  background: rgba(255, 255, 255, 0.8);
  border: 2px solid rgba(0, 191, 255, 0.2);
  color: var(--primary-blue);
  font-size: 1.4rem;
  cursor: pointer;
  padding: 0.4rem;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 191, 255, 0.1);
    border-color: var(--primary-blue);
    transform: scale(1.1) rotate(90deg);
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SidebarNavLink = styled.button`
  background: ${(props) =>
    props.active
      ? "linear-gradient(90deg, rgba(0, 191, 255, 0.1), rgba(30, 144, 255, 0.05))"
      : "none"};
  border: none;
  color: ${(props) =>
    props.active ? "var(--primary-blue)" : "var(--text-secondary)"};
  font-weight: ${(props) => (props.active ? "600" : "500")};
  font-size: 1.05rem;
  padding: 1.2rem 1.5rem;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 4px solid
    ${(props) => (props.active ? "var(--primary-blue)" : "transparent")};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 191, 255, 0.05),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    background: linear-gradient(
      90deg,
      rgba(0, 191, 255, 0.08),
      rgba(30, 144, 255, 0.03)
    );
    color: var(--primary-blue);
    border-left-color: rgba(0, 191, 255, 0.5);
    transform: translateX(8px);

    &::before {
      left: 100%;
    }
  }
`;

const SidebarContactButton = styled.button`
  background: linear-gradient(135deg, #00bfff, #1e90ff, #0066cc);
  color: white;
  padding: 1.2rem 1.5rem;
  margin: 1.5rem;
  border-radius: 15px;
  font-weight: 600;
  font-size: 1.05rem;
  border: none;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 6px 20px rgba(0, 191, 255, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    background: linear-gradient(135deg, #1e90ff, #0066cc, #004499);
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 191, 255, 0.4);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }
`;

// Floating action elements for enhanced interactivity
const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(0, 191, 255, 0.03) 0%,
      transparent 70%
    );
    animation: float 20s infinite ease-in-out;
  }

  &::before {
    top: -100px;
    left: -100px;
    animation-delay: 0s;
  }

  &::after {
    bottom: -100px;
    right: -100px;
    animation-delay: 10s;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px) rotate(0deg);
    }
    33% {
      transform: translateY(-20px) rotate(120deg);
    }
    66% {
      transform: translateY(10px) rotate(240deg);
    }
  }
`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  const navItems = [
    { name: "Home", id: "home" },
    { name: "Gallery", id: "gallery" },
    { name: "Products", id: "products" },
    { name: "Technology", id: "technology" },
    { name: "About", id: "about" }
  ];

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Get active section based on scroll position
  const getActiveSection = () => {
    const sections = navItems.map(item => item.id);
    const scrollPosition = window.scrollY + 100; // Offset for header

    for (let i = sections.length - 1; i >= 0; i--) {
      const section = document.getElementById(sections[i]);
      if (section && section.offsetTop <= scrollPosition) {
        return sections[i];
      }
    }
    return "home";
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
      setActiveSection(getActiveSection());
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (item) => {
    setSidebarOpen(false);
    scrollToSection(item.id);
  };

  const handleContactClick = () => {
    setSidebarOpen(false);
    scrollToSection("contact");
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [sidebarOpen]);

  return (
    <>
      <HeaderContainer isScrolled={isScrolled}>
        <FloatingElements />
        <Nav>
          <Logo onClick={() => scrollToSection("home")}>
            <img
              src={LogoImage}
              alt="ASKK International"
              className="logo-image"
            />
            <span className="logo-text">ASKK</span>
          </Logo>

          <DesktopNavLinks>
            {navItems.map((item) => (
              <NavLink
                key={item.name}
                active={activeSection === item.id}
                onClick={() => handleNavClick(item)}
              >
                {item.name}
              </NavLink>
            ))}
            <ContactButton onClick={handleContactClick}>Contact</ContactButton>
          </DesktopNavLinks>

          <SidebarToggle onClick={toggleSidebar}>
            {sidebarOpen ? "×" : "☰"}
          </SidebarToggle>
        </Nav>
      </HeaderContainer>

      <SidebarOverlay isOpen={sidebarOpen} onClick={closeSidebar} />

      <Sidebar isOpen={sidebarOpen}>
        <SidebarHeader>
          <SidebarLogo>
            <img
              src={LogoImage}
              alt="ASKK International"
              className="logo-image"
            />
            <span className="logo-text">ASKK</span>
          </SidebarLogo>
          <SidebarCloseButton onClick={closeSidebar}>×</SidebarCloseButton>
        </SidebarHeader>

        <SidebarContent>
          {navItems.map((item) => (
            <SidebarNavLink
              key={item.name}
              active={activeSection === item.id}
              onClick={() => handleNavClick(item)}
            >
              {item.name}
            </SidebarNavLink>
          ))}
        </SidebarContent>

        <SidebarContactButton onClick={handleContactClick}>
          Contact Us
        </SidebarContactButton>
      </Sidebar>
    </>
  );
};

export default Header;
